import type { TenantsFilterQueryParameters } from "@octopusdeploy/octopus-server-client";
import { type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useMemo } from "react";
import type { TenantsPageQueryParams } from "~/areas/tenants/Tenants/TenantsPage";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
export type TenantsFilterState = TenantsFilterQueryParameters & {
    pageNumber: number;
};
export function useTenantsFilterState(queryParams: TenantsPageQueryParams, setQueryParams: QueryParamValuesSetter<TenantsPageQueryParams>): [
    TenantsFilterState,
    (filter: TenantsFilterState) => void
] {
    const customFieldsEnabled = isFeatureToggleEnabled("ExtendedTagSetsFeatureToggle");
    function setTenantsFilterState(filter: TenantsFilterState) {
        setQueryParams({
            search: filter.filterByName,
            excludedSearch: filter.filterByExcludedName,
            project: filter.filterByProject,
            excludedProject: filter.filterByExcludedProject,
            environment: filter.filterByEnvironment,
            excludedEnvironment: filter.filterByExcludedEnvironment,
            tags: customFieldsEnabled ? [] : filter.filterByTags,
            excludedTags: customFieldsEnabled ? [] : filter.filterByExcludedTags,
            customFields: customFieldsEnabled ? filter.filterByCustomFields : [],
            excludedCustomFields: customFieldsEnabled ? filter.filterByExcludedCustomFields : [],
            includeMissingVariablesStatus: filter.includeMissingVariablesStatus,
            pageNumber: filter.pageNumber,
        });
    }
    const tenantsFilterState = useMemo((): TenantsFilterState => ({
        filterByName: queryParams.search,
        filterByExcludedName: queryParams.excludedSearch,
        filterByProject: queryParams.project,
        filterByExcludedProject: queryParams.excludedProject,
        filterByEnvironment: queryParams.environment,
        filterByExcludedEnvironment: queryParams.excludedEnvironment,
        filterByTags: customFieldsEnabled ? [] : queryParams.tags,
        filterByExcludedTags: customFieldsEnabled ? [] : queryParams.excludedTags,
        filterByCustomFields: customFieldsEnabled ? queryParams.customFields : [],
        filterByExcludedCustomFields: customFieldsEnabled ? queryParams.excludedCustomFields : [],
        includeMissingVariablesStatus: queryParams.includeMissingVariablesStatus,
        pageNumber: queryParams.pageNumber ?? 1,
    }), [
        queryParams.search,
        queryParams.excludedSearch,
        queryParams.project,
        queryParams.excludedProject,
        queryParams.environment,
        queryParams.excludedEnvironment,
        queryParams.tags,
        queryParams.excludedTags,
        queryParams.customFields,
        queryParams.excludedCustomFields,
        queryParams.includeMissingVariablesStatus,
        queryParams.pageNumber,
        customFieldsEnabled,
    ]);
    return [tenantsFilterState, setTenantsFilterState];
}
